// Procedural Staunton-style chess pieces: lathe-turned bodies (16 segments),
// an extruded knight head, brushed-gold finials. Light ivory vs charcoal.
// Geometry and materials are built once and shared by every piece on every
// board (cheap on Quest: 1–3 meshes per piece).
//
// To use her own Blender models later: replace `ChessPiece` with a GLB
// lookup (same props, origin at the bottom centre of the piece, ~0.05–0.09 m
// tall, knights facing +X before the per-side turn below).

import React from 'react';
import * as THREE from 'three';
import { noRaycast } from '../../VR/vrUi';

type P = [number, number];

/** Points around an arc in the (radius, y) plane, from angle a0 to a1 (radians). */
function arc(cy: number, rx: number, ry: number, a0: number, a1: number, n = 6): P[] {
  const out: P[] = [];
  for (let i = 0; i <= n; i++) {
    const a = a0 + ((a1 - a0) * i) / n;
    out.push([Math.max(0, Math.cos(a) * rx), cy + Math.sin(a) * ry]);
  }
  return out;
}

const BASE: P[] = [[0, 0], [0.024, 0], [0.0245, 0.004], [0.022, 0.007], [0.0215, 0.01], [0.017, 0.013]];

const PROFILES: Record<string, P[]> = {
  p: [...BASE, [0.011, 0.016], [0.0075, 0.028], [0.0125, 0.03], [0.0125, 0.032], [0.007, 0.034], ...arc(0.0425, 0.0095, 0.0095, -1.2, Math.PI / 2)],
  r: [...BASE, [0.0155, 0.016], [0.0135, 0.046], [0.018, 0.048], [0.018, 0.063], [0.0125, 0.063], [0.0125, 0.058], [0, 0.058]],
  b: [...BASE, [0.0125, 0.016], [0.008, 0.046], [0.0135, 0.048], [0.0135, 0.05], [0.008, 0.052], ...arc(0.064, 0.0115, 0.016, -1.25, Math.PI / 2, 8)],
  q: [...BASE, [0.0135, 0.016], [0.009, 0.056], [0.0155, 0.059], [0.0155, 0.061], [0.0095, 0.063], [0.0165, 0.075], [0.012, 0.0765], [0, 0.072]],
  k: [...BASE, [0.0135, 0.016], [0.0095, 0.061], [0.016, 0.064], [0.016, 0.066], [0.0105, 0.068], [0.0145, 0.079], [0, 0.081]],
  n: [...BASE, [0.0145, 0.016], [0.0135, 0.02], [0.016, 0.022], [0, 0.022]],
};

const geos = new Map<string, THREE.BufferGeometry>();
function geo(key: string, make: () => THREE.BufferGeometry) {
  let g = geos.get(key);
  if (!g) { g = make(); geos.set(key, g); }
  return g;
}
const body = (k: string) => geo(`body-${k}`, () => new THREE.LatheGeometry(PROFILES[k].map(([r, y]) => new THREE.Vector2(r, y)), 16));

/** Knight head: a side silhouette (nose toward +X) extruded 15 mm, centred. */
const knightHead = () => geo('knight-head', () => {
  const s = new THREE.Shape();
  const pts: P[] = [[-0.012, 0.02], [0.011, 0.02], [0.009, 0.032], [0.02, 0.043], [0.022, 0.05], [0.017, 0.055], [0.006, 0.058],
    [0.003, 0.067], [-0.002, 0.062], [-0.008, 0.065], [-0.012, 0.058], [-0.016, 0.046], [-0.016, 0.032]];
  s.moveTo(pts[0][0], pts[0][1]);
  for (const [x, y] of pts.slice(1)) s.lineTo(x, y);
  s.closePath();
  const g = new THREE.ExtrudeGeometry(s, { depth: 0.015, bevelEnabled: true, bevelThickness: 0.002, bevelSize: 0.0015, bevelSegments: 1, steps: 1 });
  g.translate(0, 0, -0.0075);
  g.computeVertexNormals();
  return g;
});
const ball = (r: number) => geo(`ball-${r}`, () => new THREE.SphereGeometry(r, 10, 8));
const box = (w: number, h: number, d: number) => geo(`box-${w}-${h}-${d}`, () => new THREE.BoxGeometry(w, h, d));

const mats: Record<string, THREE.Material> = {};
function mat(key: 'ivory' | 'charcoal' | 'gold'): THREE.Material {
  if (!mats[key]) {
    mats[key] = key === 'ivory' ? new THREE.MeshStandardMaterial({ color: '#efe4cc', roughness: 0.42 })
      : key === 'charcoal' ? new THREE.MeshStandardMaterial({ color: '#25292d', roughness: 0.35, metalness: 0.15 })
      : new THREE.MeshStandardMaterial({ color: '#b8862e', roughness: 0.32, metalness: 0.75 });
  }
  return mats[key];
}

/** One chess piece standing at its origin. kind: p n b r q k; side 0 = White (faces −Z), 1 = Black (faces +Z). */
export function ChessPiece({ kind, side }: { kind: string; side: 0 | 1 }) {
  const m = mat(side === 0 ? 'ivory' : 'charcoal');
  const gold = mat('gold');
  const k = PROFILES[kind] ? kind : 'p';
  return (
    <group>
      <mesh geometry={body(k)} material={m} castShadow raycast={noRaycast as any} />
      {k === 'n' && (
        // Nose toward the opponent: +X turned to −Z for White, +Z for Black.
        <group rotation={[0, side === 0 ? Math.PI / 2 : -Math.PI / 2, 0]}>
          <mesh geometry={knightHead()} material={m} castShadow raycast={noRaycast as any} />
          <mesh geometry={ball(0.0022)} material={gold} position={[0.008, 0.054, 0.0085]} raycast={noRaycast as any} />
          <mesh geometry={ball(0.0022)} material={gold} position={[0.008, 0.054, -0.0085]} raycast={noRaycast as any} />
        </group>
      )}
      {k === 'b' && <mesh geometry={ball(0.0042)} material={gold} position={[0, 0.083, 0]} raycast={noRaycast as any} />}
      {k === 'q' && <mesh geometry={ball(0.0055)} material={gold} position={[0, 0.078, 0]} raycast={noRaycast as any} />}
      {k === 'k' && <>
        <mesh geometry={box(0.0045, 0.018, 0.0045)} material={gold} position={[0, 0.089, 0]} raycast={noRaycast as any} />
        <mesh geometry={box(0.013, 0.0045, 0.0045)} material={gold} position={[0, 0.091, 0]} raycast={noRaycast as any} />
      </>}
    </group>
  );
}

export default ChessPiece;
